import React from 'react';
import { Box, Typography, Button, Paper, CircularProgress, alpha } from '@mui/material';
import {
  FileDownloadRounded as DownloadIcon,
  DescriptionRounded as DocumentIcon,
  ReceiptLongRounded as InvoiceIcon,
  HourglassEmptyRounded as PreparingIcon,
} from '@mui/icons-material';
import { DownloadDetails } from '../types/download.types';

interface DownloadCardProps {
  download: DownloadDetails;
  disabled?: boolean;
}

export const DownloadCard: React.FC<DownloadCardProps> = ({ download, disabled = false }) => {
  if (!download) return null;

  const isComplete =
    download.mode === 'sync' ? Boolean(download.url) : download.status === 'complete' && Boolean(download.url);

  const isPreparing = download.mode === 'async' && !isComplete;

  const handleDownload = () => {
    if (disabled || !download.url) return;
    window.open(download.url, '_blank', 'noopener,noreferrer');
  };

  const title =
    download.label ||
    (download.type
      ? download.type
          .replace(/[_-]/g, ' ')
          .split(' ')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ')
      : 'Download File');

  const renderIcon = () => {
    if (isPreparing) {
      return <PreparingIcon sx={{ fontSize: 20, color: 'primary.main' }} />;
    }
    const typeLower = (download.type || '').toLowerCase();
    if (typeLower.includes('invoice')) {
      return <InvoiceIcon sx={{ fontSize: 20, color: 'primary.main' }} />;
    }
    if (typeLower.includes('report') || typeLower.includes('document')) {
      return <DocumentIcon sx={{ fontSize: 20, color: 'primary.main' }} />;
    }
    return <DownloadIcon sx={{ fontSize: 20, color: 'primary.main' }} />;
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: '14px 16px',
        mt: 1.5,
        mb: 0.5,
        borderRadius: '12px',
        backgroundColor: '#FFFFFF',
        borderColor: 'rgba(0, 0, 0, 0.08)',
        borderLeft: theme => `4px solid ${theme.palette.primary.main}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: isComplete ? 1.5 : 0.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          {renderIcon()}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: '13.5px',
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.3,
              letterSpacing: '-0.1px',
            }}
          >
            {title}
          </Typography>

          {download.shipmentNumber && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '11.5px',
                color: 'text.secondary',
                lineHeight: 1.3,
                display: 'block',
                mt: 0.25,
              }}
            >
              Shipment #{download.shipmentNumber}
            </Typography>
          )}

          {isComplete && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '11.5px',
                color: 'success.main',
                fontWeight: 500,
                lineHeight: 1.3,
                display: 'block',
                mt: 0.25,
              }}
            >
              Ready to download
            </Typography>
          )}

          {isPreparing && (
            <Box sx={{ mt: 0.75 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '12px',
                  color: 'text.secondary',
                  lineHeight: 1.4,
                  display: 'block',
                  mb: 1,
                }}
              >
                Your report is taking a little longer to generate. You can continue using Copilot while we prepare it.
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '6px',
                  backgroundColor: theme => alpha(theme.palette.primary.main, 0.06),
                  border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                }}
              >
                <CircularProgress size={12} sx={{ color: 'primary.main' }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'primary.main',
                    letterSpacing: '0.2px',
                  }}
                >
                  Status: Preparing
                </Typography>
              </Box>
            </Box>
          )}

          {!isComplete && !isPreparing && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '11.5px',
                color: 'warning.main',
                fontWeight: 500,
                lineHeight: 1.3,
                display: 'block',
                mt: 0.25,
              }}
            >
              File is currently unavailable
            </Typography>
          )}
        </Box>
      </Box>

      {isComplete && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'flex-end',
            pt: 0.5,
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={handleDownload}
            disabled={disabled || !download.url}
            startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              fontSize: '11.5px',
              fontWeight: 500,
              px: 2,
              py: 0.6,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: theme => `0 2px 6px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                backgroundColor: 'primary.dark',
                boxShadow: theme => `0 3px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
              },
            }}
          >
            Download
          </Button>
        </Box>
      )}
    </Paper>
  );
};
